import dayjs from 'dayjs';
import { cloneDeep } from 'lodash';
import { isNil, uniq } from 'ramda';
import { ApiResponse } from '../@types/ApiResponse';
import { CurrentUser } from '../@types/ApiResponse/CurrentUser';
import { OrgUser } from '../@types/ApiResponse/OrgUser';
import {
  getUserByDepartment,
  getSupervisorByDepartment,
  getDepartmentByUser,
} from '@/core/src/shared/org';
import type { OrgGroups } from '@/core/src/@types/ApiResponse/OrgGroups';
import { diffSymbol } from './constant';

export const getDefaultValueType = (
  v: string,
  options: {
    userMap: Record<string, OrgUser.OrgUserItem>;
    groupMap: Record<string, OrgGroups.OrgGroupsItem>;
  },
) => {
  if (
    ['updater', 'creator'].includes(v) ||
    v.startsWith('prop') ||
    v.startsWith('status') ||
    v.startsWith('pStatus')
  )
    return 'prop';

  if (options.userMap[v]) return 'user';

  if (v.startsWith('group_')) {
    const groupId = v.replace('group_', '');
    if (options.groupMap[groupId]) return 'group';
    else {
      return 'department';
    }
  }

  if (v.startsWith('supervisor_')) return 'supervisor';

  if (v.startsWith('dynamicDepartment_')) return 'dynamicDepartment';

  return 'unknown';
};

type UserDefaultValueType =
  | 'creator'
  | 'updater'
  | `status${number}`
  | `prop${number}`
  | `group_${number}`
  | `${string[32]}`
  | `pStatus${number}`;
const isAccountId = (v: string) => /^[0-9a-zA-Z]{32}$/g.test(v);

const handleUserProp = (statusPropInfo: any, tempProp: any, template: any) => {
  try {
    const pIndex = statusPropInfo.defaultValue.split('prop');

    const index: number = Number(pIndex[1]);
    let value = tempProp[index];
    if (isNil(value)) {
      const propInfo = template.prop[index];
      if ('defaultFormula' in propInfo && propInfo.defaultFormula.type === 'const') {
        value = propInfo.defaultFormula.const;
      } else if (
        'defaultValue' in propInfo &&
        typeof propInfo.defaultValue === 'string' &&
        isAccountId(propInfo.defaultValue)
      ) {
        value = propInfo.defaultValue;
      }
    }
    if (statusPropInfo.multiple) {
      return Array.isArray(value) ? value : [value];
    } else {
      return Array.isArray(value) ? value[0] : value;
    }
  } catch (error) {
    return null;
  }
};
const getUserIds = (mul: boolean, userList: any[]) => {
  if (mul) return userList.map((x: any) => x.account_id);
  if (!userList.length) return null;
  return userList[0]['account_id'] || null;
};

export const handleSingleStatusDefaultValue = (
  template: CurrentUser.TemplateInfo,
  statusPropInfo: {
    multiple: boolean;
    defaultValue: UserDefaultValueType;
    index: number;
    historyStatusInfo?: any[];
  },
  tempProp: any[],
  creator: string,
  participator: string[],
  userId: string,
  groupMap: Record<string, any>,
  userMap: Record<string, OrgUser.OrgUserItem> = {},
  departmentNodes: OrgGroups.OrgDepartmentNodeInfo[] = [],
) => {
  // 创建者
  if (statusPropInfo.defaultValue === 'creator') {
    return statusPropInfo.multiple ? [creator] : creator;
    // 更新者
  } else if (statusPropInfo.defaultValue === 'updater') {
    return statusPropInfo.multiple ? [userId] : userId;
    // 关联属性值
  } else if (isAccountId(statusPropInfo.defaultValue)) {
    //指定人员
    return statusPropInfo.defaultValue;
  } else if (
    statusPropInfo &&
    statusPropInfo.defaultValue &&
    statusPropInfo.defaultValue.startsWith('status')
  ) {
    //指定状态的负责人
    const statusIndex = Number(statusPropInfo.defaultValue.toLowerCase().split('status')[1]);

    if (statusPropInfo && statusPropInfo.defaultValue) {
      const lastStatusInfo = statusPropInfo.historyStatusInfo?.find((x: any) => x.s == statusIndex);
      let val = lastStatusInfo && lastStatusInfo['p'] ? lastStatusInfo['p'][0] : null;
      return statusPropInfo.multiple ? [val] : val;
    }
  } else if (
    statusPropInfo &&
    statusPropInfo.defaultValue &&
    statusPropInfo.defaultValue.startsWith('pStatus')
  ) {
    //指定状态的参与者
    const statusIndex = statusPropInfo.defaultValue.split('pStatus')[1];
    const settingStatus = template?.task_status.find((x: any) => x.index == statusIndex);

    if (
      settingStatus &&
      statusPropInfo &&
      (statusPropInfo.index == 0 || statusPropInfo.index == 1)
    ) {
      const newPropInfo = settingStatus.prop[statusPropInfo.index];

      if (statusPropInfo && statusPropInfo.defaultValue) {
        const lastStatusInfo = statusPropInfo.historyStatusInfo?.find(
          (x: any) => x.s == statusIndex,
        );

        const val = lastStatusInfo && lastStatusInfo['p'] ? lastStatusInfo['p'][1] : [];
        return statusPropInfo.multiple ? val : val.length ? val[0] : null;
      } else if (newPropInfo && participator.length && newPropInfo.type === 'user') {
        return statusPropInfo.multiple ? participator : participator[0];
      } else {
        return null;
      }
    }
  } else if (
    statusPropInfo &&
    statusPropInfo.defaultValue &&
    statusPropInfo.defaultValue.startsWith('prop')
  ) {
    //获取人员属性的值

    return handleUserProp(statusPropInfo, tempProp, template);
  } else if (
    statusPropInfo &&
    statusPropInfo.defaultValue &&
    statusPropInfo.defaultValue.startsWith('group_')
  ) {
    //负责人或者参与者是成员组
    const groupType = getDefaultValueType(statusPropInfo.defaultValue, { userMap, groupMap });
    if (groupType === 'group') {
      const [_, groupId] = statusPropInfo.defaultValue.split('_');
      let userList = groupMap[groupId];
      if (statusPropInfo?.index == 0 || !statusPropInfo?.multiple) {
        return userList?.[0];
      } else {
        return userList;
      }
    } else if (groupType === 'department') {
      //根据人员的部门信息获取id
      const [_, gid] = statusPropInfo.defaultValue.split('_');
      const users = getUserByDepartment(gid, userMap);
      return getUserIds(statusPropInfo.multiple, users);
    }
  } else if (statusPropInfo?.defaultValue && ~statusPropInfo.defaultValue.indexOf('supervisor_')) {
    //主管信息获取人员信息
    const [_, userType, levelStr] = statusPropInfo.defaultValue.split('_');
    const level = Number(levelStr);

    let suId = '';
    if (userType === 'creator') {
      //创建者
      suId = creator;
    } else {
      //人员属性
      suId = handleUserProp(
        { defaultValue: userType, index: statusPropInfo.index, multiple: statusPropInfo.multiple },
        tempProp,
        template,
      );
    }
    const suIds = Array.isArray(suId) ? suId : [suId];

    const departmentIds = Array.from(
      new Set(
        suIds
          .map((id) => getDepartmentByUser(userMap[id], departmentNodes, level))
          .flat()
          .map((i) => i.id),
      ),
    );

    return Array.from(
      new Set(
        departmentIds
          .map((departmentId) => getSupervisorByDepartment(departmentId, departmentNodes, userMap))
          .flat()
          .map((i) => i.account_id),
      ),
    );
  } else {
    // 用户id
    return statusPropInfo.multiple ? [statusPropInfo.defaultValue] : statusPropInfo.defaultValue;
  }
};

export const getDefaultStatus = (template: ApiResponse.CurrentUser.TemplateInfo) => {
  const firstStatus = 0;
  const { task_status } = template;
  if (Array.isArray(task_status) && task_status.length) {
    const states: (ApiResponse.CurrentUser.taskStatus & { index: number })[] = [];
    task_status.map((s, i) => {
      if (s && !s.delete) {
        // FIXME: mutable
        s.index = i;
        s.sort = 'sort' in s ? s.sort : i;
        states.push({
          ...s,
          index: i,
        });
      }
    });
    if (states.length) {
      states.sort((b, a) => b.sort - a.sort);
      return states[0].index;
    }
  }
  return firstStatus;
};

export const handleStatusUserProp = (
  template: TemplateInfo,
  propInfo: {
    multiple: boolean;
    defaultValue: UserDefaultValueType | UserDefaultValueType[];
    index: number;
    historyStatusInfo: any;
  },
  tempProp: any[],
  creator: string,
  participator: string[],
  userId: string,
  groupMap: Record<string, any>,
  userMap: Record<string, OrgUser.OrgUserItem>,
  departmentNodes: OrgGroups.OrgDepartmentNodeInfo[],
): any => {
  if (Array.isArray(propInfo.defaultValue)) {
    const statusValue = propInfo.defaultValue.map((x: any) => {
      return handleSingleStatusDefaultValue(
        template,
        {
          multiple: propInfo?.multiple,
          defaultValue: x,
          index: propInfo?.index,
          historyStatusInfo: propInfo.historyStatusInfo,
        },
        tempProp,
        creator,
        participator,
        userId,
        groupMap,
        userMap,
        departmentNodes,
      );
    });

    const val = statusValue?.length
      ? statusValue.reduce((a: any, b: any) => {
          if (b) {
            if (Array.isArray(b)) {
              b.forEach((c) => {
                c && a.indexOf(c) == -1 ? a.push(c) : null;
              });
            } else {
              a.indexOf(b) > -1 ? null : a.push(b);
            }
          }
          return a;
        }, [])
      : [];
    // return val;
    if (propInfo.index == 0) {
      //负责人
      return Array.isArray(val) && val.length ? val[0] : null;
    } else if (propInfo.index == 1) {
      //参与者
      return val;
    }
  } else {
    return handleSingleStatusDefaultValue(
      template,
      propInfo as any,
      tempProp,
      creator,
      participator,
      userId,
      groupMap,
      userMap,
      departmentNodes,
    );
  }
};

export const generateGroupMap = (userMap: Record<string, any>) => {
  if (userMap && Object.keys(userMap).length) {
    let newGroupMap: Record<string, string[]> = {};

    Object.keys(userMap).forEach((x) => {
      let curUserInfo = userMap[x];
      if (curUserInfo.group_ids && Array.isArray(curUserInfo.group_ids)) {
        curUserInfo.group_ids.map((g: any) => {
          if (newGroupMap[g]) {
            newGroupMap[g].indexOf(curUserInfo.account_id) > -1
              ? null
              : newGroupMap[g].push(curUserInfo.account_id);
          } else {
            newGroupMap[g] = [curUserInfo.account_id];
          }
        });
      } else {
        newGroupMap['-1']
          ? newGroupMap['-1'].push(curUserInfo.account_id)
          : (newGroupMap['-1'] = [curUserInfo.account_id]);
      }
    });
    return newGroupMap;
  }
  return {};
};

type NodeInfo = {
  tempProp: any[];
  tempStatusProp: any[];
  creator: string;
  createTime: number | undefined;
  participator?: string[];
  useDefault?: boolean;
  historyStatusInfo: any;
};
/**
 * getDefaultStatusProp 获取节点状态属性的默认值
 * @param {object} nodeInfo  { tempProp, creator, createTime }
 * @param {number} index      状态index
 * @param {object} template   模板配置
 * @param {string} userId     用户id
 * @param {array} initStatusProp 需要在初始化时赋值
 */
export const getDefaultStatusProp = (
  nodeInfo: NodeInfo,
  index: number,
  template: ApiResponse.CurrentUser.TemplateInfo,
  userId: string,
  initStatusProp: any[] = [],
  userMap: any,
  departmentNodes: OrgGroups.OrgDepartmentNodeInfo[] = [],
) => {
  const { tempProp, tempStatusProp = [], creator, createTime, participator = [] } = nodeInfo;
  if (!template?.task_status[index]) {
    return [];
  }

  // NOTE: task_status 可能存在被删除的数据
  const statusPropInfo = template?.task_status[index].prop;
  const groupMap = generateGroupMap(userMap);

  let data = statusPropInfo.map((propInfo, statusPropIndex) => {
    // 模板状态属性人员 creator创建者 updater修改者 prop[X]模板属性的位置
    if (!propInfo || propInfo.display === false) {
      return null;
    }

    // 人员属性
    if (propInfo.type === 'user' && propInfo.defaultValue) {
      const _propInfo: any = { ...propInfo };
      if (!('index' in propInfo)) {
        _propInfo.index = statusPropIndex;
      }

      if (nodeInfo && nodeInfo.historyStatusInfo) {
        _propInfo.historyStatusInfo = nodeInfo.historyStatusInfo;
      }

      return handleStatusUserProp(
        template,
        _propInfo,
        tempProp,
        creator,
        participator,
        userId,
        groupMap,
        userMap,
        departmentNodes,
      );
    }
    // 时间属性 createTime创建时间 updateTime修改时间
    if (propInfo.type === 'datetime' && propInfo.defaultValue) {
      // 创建时间
      if (propInfo.defaultValue === 'createTime' && createTime) {
        return dayjs(createTime).valueOf();
        // 更新时间
      } else if (propInfo.defaultValue === 'updateTime') {
        return dayjs().valueOf();
      } else if (propInfo.defaultValue === 'previousStart') {
        return tempStatusProp[2];
      } else if (propInfo.defaultValue === 'previousEnd') {
        return tempStatusProp[3];
      } else if (
        typeof propInfo.defaultValue === 'string' &&
        propInfo.defaultValue.startsWith('prop')
      ) {
        const propIndex = Number(propInfo.defaultValue.replace('prop', ''));
        return nodeInfo.tempProp[propIndex];
      } else {
        return dayjs().valueOf();
      }
    }
    // 关联属性值 直接赋值null
    if (propInfo.type === 'quote') {
      return null;
    }
    return null;
  });

  // 初始化赋值 覆盖默认值
  if (initStatusProp) {
    initStatusProp.map((x, i) => {
      if (x !== null) {
        data[i] = x;
      }
    });
  }

  return data;
};

export const hasRequiredStatusProp = (statusProp: ApiResponse.CurrentUser.statusProp[]) => {
  return !!statusProp.filter((x) => x && x.required && x.display && x.type !== 'quote').length;
};

export const getStatusRouteConfig = (
  currentStatusIndex: number,
  targetStatusIndex: number,
  templateInfo: CurrentUser.TemplateInfo,
) => {
  const currentStatusConfig = templateInfo?.task_status[currentStatusIndex];
  const routeConfig = currentStatusConfig?.flowSetting?.find((f) => f.index === targetStatusIndex);

  if (!routeConfig) return undefined;

  return routeConfig;
};

/**
 * 多个节点同时切换状态时，可保留差异的状态属性
 * 差异属性标记 Symbol('diff')
 * return Symbol('diff') defaultStatusProp defaultStatusPropDiff
 *
 * @param {*} nodeInfos [{ tempProp, creator, createTime }]
 * @param {*} index 状态index
 * @param {*} template  模板属性
 * @param {*} userId  用户id
 * @returns any[]
 */
export const getNodesDiffStatusProp = (
  nodeInfos: NodeInfo[],
  index: number = 0,
  template: ApiResponse.CurrentUser.TemplateInfo,
  userId: string,
  userMap: Record<string, OrgUser.OrgUserItem>,
) => {
  const allStatusProps = nodeInfos.map((x) =>
    // QUESTION: 批量转状态，历史状态无法读取
    getDefaultStatusProp({ ...x, historyStatusInfo: [] }, index, template, userId, [], userMap),
  );
  let defaultStatusProp = cloneDeep(allStatusProps[0]);

  const defaultStatusPropDiff = template?.task_status[index]?.prop?.map?.((x, i) => {
    // 人员是创建者、引用，默认值可能不同
    if (x.type === 'user' && x.defaultValue) {
      if (x.defaultValue === 'creator' || ~x.defaultValue.indexOf('prop')) {
        defaultStatusProp[i] = diffSymbol;
        let diffName = '默认值：';
        if (x.defaultValue === 'creator') {
          diffName += '创建者';
        }
        if (~x.defaultValue.indexOf('prop')) {
          const propIndex = x.defaultValue.split('prop')[1];
          diffName += template.prop[Number(propIndex)].name;
        }
        return {
          values: allStatusProps.map((x) => x[i]),
          config: x,
          diffName,
        };
      }
    }
    // 时间是创建者，默认值可能不同
    if (x.type === 'datetime' && x.defaultValue) {
      if (x.defaultValue === 'createTime') {
        defaultStatusProp[i] = diffSymbol;
        return {
          values: allStatusProps.map((x) => x[i]),
          config: x,
          diffName: '默认值：创建时间',
        };
      }
    }
    return null;
  });

  return { diffSymbol, defaultStatusProp, defaultStatusPropDiff };
};

/**
 * 转换批量状态属性为api数据格式
 * @param {*} orgId  空间id
 * @param {*} nodeIds  批量处理的节点id[]
 * @param {*} tempId 模板id
 * @param {*} status 状态
 * @param {*} statusProp 状态属性
 * @param {*} defaultStatusPropDiff 差异的状态属性的默认值
 * @returns {object} {[key:string]:[value:any]}
 */
export const transformBatchStatusDataToApiData = (
  orgId: string,
  nodeIds: string[],
  tempId: string,
  status: number,
  statusProp: any[],
  defaultStatusPropDiff: ReturnType<typeof getNodesDiffStatusProp>['defaultStatusPropDiff'],
) => {
  let data: any = {
    record: true,
    org_id: orgId,
    temp_id: tempId,
    status: status,
    node: nodeIds,
  };
  // 如果有diffSymbol，处理数据到nodeProp字段
  if (!~statusProp.indexOf(diffSymbol)) {
    data = {
      ...data,
      owner: statusProp[0] ? [statusProp[0]] : null,
      user: statusProp[1] || null,
      startTime: statusProp[2] ? dayjs(statusProp[2]).valueOf() : null,
      endTime: statusProp[3] ? dayjs(statusProp[3]).valueOf() : null,
    };
    if (typeof statusProp[4] === 'string') data.statusCommit = statusProp[4];
  } else {
    const nodeProp: any = {};
    nodeIds.map((id, i) => {
      const nodePropItem: any = {
        owner: null,
        user: null,
        startTime: null,
        endTime: null,
      };
      if (statusProp[0] === diffSymbol) {
        nodePropItem.owner = defaultStatusPropDiff[0]?.values[i]
          ? defaultStatusPropDiff[0].values[i]
          : null;
      } else {
        nodePropItem.owner = statusProp[0] ? statusProp[0] : null;
      }

      if (statusProp[1] === diffSymbol) {
        nodePropItem.user = defaultStatusPropDiff[1]?.values[i]
          ? defaultStatusPropDiff[1].values[i]
          : null;
      } else {
        nodePropItem.user = statusProp[1] || null;
      }

      if (statusProp[2] === diffSymbol) {
        nodePropItem.startTime = defaultStatusPropDiff[2]?.values[i]
          ? dayjs(defaultStatusPropDiff[2].values[i]).valueOf()
          : null;
      } else {
        nodePropItem.startTime = statusProp[2] ? dayjs(statusProp[2]).valueOf() : null;
      }

      if (statusProp[3] === diffSymbol) {
        nodePropItem.endTime = defaultStatusPropDiff[3]?.values[i]
          ? dayjs(defaultStatusPropDiff[3].values[i]).valueOf()
          : null;
      } else {
        nodePropItem.endTime = statusProp[3] ? dayjs(statusProp[3]).valueOf() : null;
      }

      if (statusProp[4] === diffSymbol) {
        nodePropItem.statusCommit = defaultStatusPropDiff[4]?.values[i]
          ? defaultStatusPropDiff[4].values[i]
          : null;
      } else if (typeof statusProp[4] === 'string') {
        nodePropItem.statusCommit = statusProp[4];
      }

      nodeProp[id] = nodePropItem;
    });

    data.nodeProp = nodeProp;
  }
  return data;
};
